import React, { forwardRef, useState } from 'react';
import { cx } from './cx';

// The `form-*` classes opt in to @tailwindcss/forms (configured with strategy 'class'), which
// gives every native control the same baseline across browsers; the rest is the kit's look.
const control =
  'block rounded-lg border-gray-300 bg-white text-sm text-gray-900 shadow-sm placeholder:text-gray-400 ' +
  'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:shadow-none dark:placeholder:text-gray-400 ' +
  'focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/30 ' +
  'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400';
const invalidControl = 'border-red-400 focus:border-red-500 focus:ring-red-500/20 dark:border-red-400 dark:focus:border-red-400 dark:focus:ring-red-400/30';

interface ControlProps {
  /** Marks the control as failing validation; pair it with Field's `error`. */
  invalid?: boolean;
  /** A shorter control for use inline in a sentence or a row; set its width with className. */
  compact?: boolean;
}

// A compact control leaves its width to the caller (or its content), e.g. `className="w-16"` for a number.
const sizing = (compact?: boolean) => (compact ? 'h-8' : 'h-9 w-full');

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & ControlProps>(
  function Input({ invalid, compact, className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cx('form-input', compact ? 'px-2' : 'px-3', sizing(compact), control, invalid && invalidControl, className)}
        {...rest}
      />
    );
  },
);

type NumberInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'min' | 'max'> &
  ControlProps & {
    value: number;
    min: number;
    max: number;
    onChange: (value: number) => void;
  };

/**
 * A whole number between min and max. It keeps its own text while being typed in, so the digits
 * can all be deleted to type a new number: each whole number in range is passed on as it's typed,
 * and on leaving the field one out of range is brought within it and an empty one goes back to
 * the last number.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput({ value, min, max, onChange, onFocus, onBlur, ...rest }, ref) {
    const [text, setText] = useState(String(value));
    const [editing, setEditing] = useState(false);

    return (
      <Input
        ref={ref}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={editing ? text : String(value)}
        onFocus={(e) => { setText(String(value)); setEditing(true); onFocus?.(e); }}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value !== '' && Number.isInteger(n) && n >= min && n <= max && n !== value) onChange(n);
        }}
        onBlur={(e) => {
          setEditing(false);
          const n = Number(text);
          if (text !== '' && Number.isFinite(n)) {
            const clamped = Math.min(max, Math.max(min, Math.round(n)));
            if (clamped !== value) onChange(clamped);
          }
          onBlur?.(e);
        }}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & Omit<ControlProps, 'compact'>>(
  function Textarea({ invalid, className, rows = 3, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={invalid || undefined}
        className={cx('form-textarea w-full px-3 py-2', control, invalid && invalidControl, className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & ControlProps>(
  function Select({ invalid, compact, className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cx('form-select', compact ? 'pl-2 pr-8' : 'pl-3 pr-9', sizing(compact), control, invalid && invalidControl, className)}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

export const Checkbox = forwardRef<HTMLInputElement, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>>(
  function Checkbox({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        type="checkbox"
        className={cx(
          'form-checkbox h-4 w-4 flex-none cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:checked:bg-indigo-500 dark:focus:ring-indigo-400 dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
    );
  },
);

export interface FieldProps {
  label: React.ReactNode;
  /** The id of the control inside, so the label is tied to it. Leave out for a group of controls. */
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Writes "optional" after the label, in place of marking the required fields. */
  optional?: boolean;
  className?: string;
  children: React.ReactNode;
}

/** A label, the control(s) under it, and a hint or error below. */
export function Field({ label, htmlFor, hint, error, optional, className, children }: FieldProps) {
  const LabelTag = htmlFor ? 'label' : 'span';
  return (
    <div className={cx('grid gap-1.5', className)}>
      <LabelTag htmlFor={htmlFor} className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {label}
        {optional && <span className="ml-1.5 font-normal text-gray-400 dark:text-gray-400">optional</span>}
      </LabelTag>
      {children}
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">{error}</p>
      ) : (
        hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  );
}
